import * as Cookies from 'js-cookie';
import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';
import { Sentry } from '@opendoor/observability';
import { accept, base, createFetch, createStack, header, init, method, params } from 'http-client';

import { alphabetize, getMarketDisplayNameWithState } from 'src/helpers/markets';

import { OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product } from '../__generated__/globalTypes';
import { WEB_API_URL } from '../helpers/globals';
import athenaClient from './athenaApi';
import { maybeHandleGraphQLError, validateSellerInput } from './athenaUtilities';
import {
  CreateSellerInputWithTrackingDataFromCookies,
  CreateSellerInputWithTrackingDataFromCookiesVariables,
} from './mutations/__generated__/CreateSellerInputWithTrackingDataFromCookies';
import {
  ReferToMarkSpainMutation,
  ReferToMarkSpainMutationVariables,
} from './mutations/__generated__/ReferToMarkSpainMutation';
import {
  ReferToOpcityMutation,
  ReferToOpcityMutationVariables,
} from './mutations/__generated__/ReferToOpcityMutation';
import {
  ReferToParnMutation,
  ReferToParnMutationVariables,
} from './mutations/__generated__/ReferToParnMutation';
import {
  ReferToRedfinMutation,
  ReferToRedfinMutationVariables,
} from './mutations/__generated__/ReferToRedfinMutation';
import createSellerInputWithTrackingDataFromCookies from './mutations/createSellerInputWithTrackingDataFromCookies';
import referToMarkSpainMutation from './mutations/referToMarkSpain';
import referToOpcityMutation from './mutations/referToOpcity';
import referToParnMutation from './mutations/referToParn';
import referToRedfinMutation from './mutations/referToRedfin';
import {
  ListMarkets as ListMarketsQueryData,
  ListMarkets_webMarket_listMarkets_market as Market,
} from './queries/__generated__/ListMarkets';
import listMarkets from './queries/listMarkets';
import { channelStringToEnum, constructLead } from './sellerInputToLead';

export const COOKIE_KEY = 'XSRF-G-TOKEN';

// N.B. this is a function because `Cookies.get` can change
// during runtime, we don't want to use the original (or non-existent)
// one from JS-load time
const commonStack = () =>
  createStack(
    init('credentials', 'include'),
    accept('application/json'),
    header('Content-Type', 'application/json'),
    header('X-CSRF-Token', Cookies.get(COOKIE_KEY)),
    base(WEB_API_URL),
  );

function doGet(url: string): Promise<Response> {
  return createFetch(commonStack())(url);
}

function doPost(url: string, body: unknown): Promise<Response> {
  return createFetch(commonStack(), method('POST'), params(body))(url);
}
export class APIError extends Error {
  public fatal?: boolean;
  constructor(fatal?: boolean, ...args: (string | undefined)[]) {
    super(...args);
    this.fatal = fatal;
    // captureStackTrace seems to be missing for some versions of Mobile Safari.
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }
}

export async function sendPasswordSetUpEmail(email: string, redirectUrl: string) {
  return doPost(`/api/v1/humans/reset-password`, { email, redirect_url: redirectUrl }).then(
    (res) => {
      if (res.ok) {
        return true;
      }

      throw new APIError(true, `unable to send password set up email ${email}`);
    },
  );
}

export async function fetchMarkets(): Promise<Array<any>> {
  const { data, errors, error } = await athenaClient.query<ListMarketsQueryData>({
    query: listMarkets,
  });

  maybeHandleGraphQLError(errors, error?.networkError, { operation: 'ListMarkets' });

  const markets = data?.webMarket?.listMarkets?.market;

  if (!markets) {
    const errMsg = 'GraphQL query ListMarkets returned no markets';
    Sentry.captureException(errMsg);
    throw new APIError(true, errMsg);
  }

  const activeSellDirectMarkets = transformMarkets(markets);

  return activeSellDirectMarkets;
}

const transformMarkets = (markets: Market[]): Array<{ label: string; value: string | null }> => {
  const activeSellDirectMarkets: Array<{ label: string; value: string | null }> = [];

  markets.forEach((market) => {
    if (market.marketData?.active) {
      const identifier = market.marketData.marketIdentifier;
      const option = { label: getMarketDisplayNameWithState(identifier), value: identifier };
      activeSellDirectMarkets.push(option);
    }
  });

  return activeSellDirectMarkets.sort(alphabetize);
};

export async function createSellerInput(
  { street1, unit, city, postal_code, state }: IAddressInputAddress,
  { channel, trackingKeywords }: { channel?: string; trackingKeywords?: string } = {},
) {
  if (trackingKeywords) {
    await trackLead(trackingKeywords);
  }

  const { data, errors } = await athenaClient.mutate<
    CreateSellerInputWithTrackingDataFromCookies,
    CreateSellerInputWithTrackingDataFromCookiesVariables
  >({
    mutation: createSellerInputWithTrackingDataFromCookies,
    variables: {
      input: {
        address: {
          city,
          postalCode: postal_code,
          state,
          street: street1,
          unit,
        },
        channel: channelStringToEnum(channel),
        analyticsMetadata: {
          addressEntry: {
            product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product.UNKNOWN,
            url: window.location.href,
          },
        },
      },
    },
  });

  maybeHandleGraphQLError(errors, undefined, {
    operation: 'CreateSellerInputWithTrackingDataFromCookies',
  });

  const sellerInput = data?.reception?.createSellerInputWithTrackingDataFromCookies?.sellerInput;

  validateSellerInput(sellerInput, {
    errMsg: 'Seller input from CreateSellerInputWithTrackingDataFromCookies response is undefined',
    fatal: true,
  });

  return constructLead(sellerInput);
}

function trackLead(trackingKeywords: string) {
  return doGet(`/track/${trackingKeywords}`);
}

export const isValueInCookie = (key: string, value: string) => {
  const cookieValue = Cookies.get(key);
  return cookieValue && cookieValue == value;
};

export async function referToOpcity(
  sellerInputUuid: string,
  fullName: string,
  email: string,
  phoneNumber: string,
) {
  const { data, errors } = await athenaClient.mutate<
    ReferToOpcityMutation,
    ReferToOpcityMutationVariables
  >({
    mutation: referToOpcityMutation,
    variables: { sellerInputUuid, fullName, email, phoneNumber },
  });

  maybeHandleGraphQLError(errors, undefined, { operation: 'ReferToOpcityMutation' });

  return data?.webAgentReferral?.referToOpcity?.result ?? false;
}

export async function referToRedfin(
  sellerInputUuid: string,
  fullName: string,
  email: string,
  phoneNumber: string,
) {
  const { data, errors } = await athenaClient.mutate<
    ReferToRedfinMutation,
    ReferToRedfinMutationVariables
  >({
    mutation: referToRedfinMutation,
    variables: { sellerInputUuid, fullName, email, phoneNumber },
  });

  maybeHandleGraphQLError(errors, undefined, { operation: 'ReferToRedfinMutation' });

  return data?.webAgentReferral?.referToRedfin?.result ?? false;
}

export async function referToMarkSpain(
  sellerInputUuid: string,
  fullName: string,
  email: string,
  phoneNumber: string,
) {
  const { data, errors } = await athenaClient.mutate<
    ReferToMarkSpainMutation,
    ReferToMarkSpainMutationVariables
  >({
    mutation: referToMarkSpainMutation,
    variables: { sellerInputUuid, fullName, email, phoneNumber },
  });

  maybeHandleGraphQLError(errors, undefined, { operation: 'ReferToMarkSpainMutation' });

  return data?.webAgentReferral?.referToMarkSpain?.success ?? false;
}

export async function referToParn(
  sellerInputUuid: string,
  fullName: string,
  email: string,
  phoneNumber: string,
) {
  const { data, errors } = await athenaClient.mutate<
    ReferToParnMutation,
    ReferToParnMutationVariables
  >({
    mutation: referToParnMutation,
    variables: { sellerInputUuid, fullName, email, phoneNumber },
  });

  maybeHandleGraphQLError(errors, undefined, { operation: 'ReferToParnMutation' });

  return data?.webAgentReferral?.referToParn?.success ?? false;
}
